import React, { type ReactNode } from 'react'
import { Modal, StyleSheet, View } from 'react-native'
import { DS } from '@/design-system'
import { getDSColorWithOpacity } from '@/design-system/Palette'
import { toastNotificationStore } from '@/components/ANewDesign/ToastNotification/ToastNotification'

interface DecisionButton {
  label: string
  onPress: () => void
  /** Distinguishes which button is currently mid-async-call. Buttons disable when ANY action is pending. */
  pendingKey?: string
}

interface Props {
  visible: boolean
  title: string
  body: ReactNode
  primary: DecisionButton
  secondary?: DecisionButton
  /** Which action (if any) is currently in flight. Disables both buttons; pass `null` when idle. */
  pendingKey?: string | null
}

/**
 * Sticky decision modal used for "this happened, now you must choose" tournament
 * prompts. Hardware back is intentionally swallowed — the surrounding server
 * logic always has an expiry/timeout that resolves the decision if the user
 * walks away, so blocking back-dismiss is safe and forces an explicit choice
 * while the modal is up. To avoid Android users thinking back is broken, we
 * surface a queued toast on every swallowed back-press so the input
 * registered as a non-action rather than a no-op.
 *
 * Style is fixed by design — primary/secondary buttons stack with consistent
 * chrome. If you need richer layouts, build a different primitive rather than
 * adding props here.
 */
export function StickyDecisionModal({ visible, title, body, primary, secondary, pendingKey }: Props) {
  if (!visible) return null
  const isBusy = pendingKey != null

  const onSwallowedBackPress = () => {
    toastNotificationStore.getState().show({ text: 'Please choose an option below.' })
  }

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onSwallowedBackPress}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <DS.Text variant="Heading 3/Medium" style={styles.title}>
            {title}
          </DS.Text>
          {typeof body === 'string' ? (
            <DS.Text variant="Body Large/Regular" style={styles.body}>
              {body}
            </DS.Text>
          ) : (
            <View style={styles.body}>{body}</View>
          )}
          <DS.Frame gap="8">
            <DS.Button
              type="On Light"
              variant="Large/Primary"
              backgroundColor="Brand/Maroon"
              onPress={primary.onPress}
              disabled={isBusy}
            >
              {primary.label}
            </DS.Button>
            {secondary && (
              <DS.Button type="On Dark" variant="Large/Secondary" onPress={secondary.onPress} disabled={isBusy}>
                {secondary.label}
              </DS.Button>
            )}
          </DS.Frame>
        </View>
      </View>
    </Modal>
  )
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: getDSColorWithOpacity('Global/Black', 0.5),
    justifyContent: 'center',
    alignItems: 'center'
  },
  card: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 16,
    padding: 24,
    width: '90%',
    maxWidth: 400
  },
  title: { textAlign: 'center', marginBottom: 12 },
  body: { textAlign: 'center', marginBottom: 24 }
})
